import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';

import { InfoCardVariants } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';

import { ArgoCDLogoSmall } from '../../../home/ArgoCDLogo';
import { ContentSection } from './ContentSection';
import { CreationStatusPanel, ProblemTooltip } from './CreationStatusPanel';
import { InlineField } from './InlineField';
import { SectionItem } from './SectionItem';
import { StatusIndicator } from './StatusIndicator';
import { TablePager } from './TablePager';
import { useApplicationSetArgocdStatus } from './hooks/useArgocdStatus';
import { useStyles } from './styles';
import {
  ANNOTATION_APPLICATION_SET_NAME,
  ANNOTATION_PROJECT_HASH,
  ANNOTATION_PROJECT_NAME,
  compactCellStyle,
  compactHeaderStyle,
  deriveOverallHealth,
  deriveOverallSync,
  formatTimestamp,
  statusIndicatorType,
} from './utils';

const ROWS_PER_PAGE = 5;

export interface ArgocdApplicationSetStatusCardProps {
  variant?: InfoCardVariants;
}

const DegradedTooltip = ({ resources }: { resources?: string[] }) => {
  const theme = useTheme();
  if (!resources || resources.length === 0) return null;
  return (
    <Tooltip
      title={
        <>
          <strong>Degraded resources:</strong>
          <br />
          {resources.map((r, i) => (
            <React.Fragment key={i}>
              {'• '}
              {r}
              <br />
            </React.Fragment>
          ))}
        </>
      }
      arrow
    >
      <InfoOutlinedIcon
        style={{
          fontSize: 16,
          color: theme.palette.error.main,
          cursor: 'help',
          marginLeft: 6,
        }}
      />
    </Tooltip>
  );
};

export const ArgocdApplicationSetStatusCard = ({
  variant,
}: ArgocdApplicationSetStatusCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const projectName =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_NAME] || '';
  const applicationSetName =
    entity.metadata.annotations?.[ANNOTATION_APPLICATION_SET_NAME] || '';
  const projectHash =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_HASH] || '';
  const appsetFullName = `${applicationSetName}-${projectHash}`;

  const missingAnnotations =
    !projectName || !applicationSetName || !projectHash;

  const { data, loading, error, refresh } = useApplicationSetArgocdStatus(
    missingAnnotations ? '' : appsetFullName,
    missingAnnotations ? '' : projectName,
  );
  const [page, setPage] = useState(0);

  const childApps = useMemo(() => data?.childApps ?? [], [data?.childApps]);

  useEffect(() => {
    setPage(0);
  }, [childApps.length]);
  const overallHealth = useMemo(
    () => deriveOverallHealth(childApps),
    [childApps],
  );
  const overallSync = useMemo(() => deriveOverallSync(childApps), [childApps]);
  const degradedProblems = useMemo(
    () =>
      childApps
        .filter(a => a.healthStatus === 'Degraded')
        .map(a => ({ name: a.name, reason: 'Degraded' })),
    [childApps],
  );
  const outOfSyncProblems = useMemo(
    () =>
      childApps
        .filter(a => a.syncStatus === 'OutOfSync')
        .map(a => ({ name: a.name, reason: 'OutOfSync' })),
    [childApps],
  );
  const paginatedApps = useMemo(
    () => childApps.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
    [childApps, page],
  );

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="ArgoCD Application Set Status"
        className={classes.cardHeader}
        action={
          <IconButton
            aria-label="Refresh"
            title="Refresh status"
            onClick={refresh}
          >
            <RefreshIcon />
          </IconButton>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        {missingAnnotations && (
          <Alert severity="info">
            Required DaaS annotations are missing from this entity. Ensure{' '}
            <code>{ANNOTATION_PROJECT_NAME}</code>,{' '}
            <code>{ANNOTATION_APPLICATION_SET_NAME}</code>, and{' '}
            <code>{ANNOTATION_PROJECT_HASH}</code> are set.
          </Alert>
        )}

        {!missingAnnotations && loading && !data && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!missingAnnotations && error && !data && (
          <Alert severity="error">
            Failed to fetch ArgoCD status: {error.message}
          </Alert>
        )}

        {!missingAnnotations && data && (
          <Box>
            <CreationStatusPanel
              kind="ApplicationSet"
              creationStatus={data.creationStatus}
              creationStatusMessage={data.creationStatusMessage}
              conditions={data.conditions}
            />

            {data.creationStatus === 'Succeeded' && (
              <>
                <ContentSection label="Summary" gridSizes={{ xs: 12 }}>
                  <SectionItem>
                    <InlineField label="Status">
                      {childApps.length > 0 ? (
                        <Box display="flex" alignItems="center">
                          <StatusIndicator
                            type={overallHealth.type}
                            label={overallHealth.label}
                          />
                          <ProblemTooltip problems={degradedProblems} />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ margin: '0 6px' }}
                          >
                            |
                          </Typography>
                          <StatusIndicator
                            type={overallSync.type}
                            label={overallSync.label}
                          />
                          <ProblemTooltip problems={outOfSyncProblems} />
                        </Box>
                      ) : (
                        <StatusIndicator type="succeeded" label="Succeeded" />
                      )}
                    </InlineField>
                  </SectionItem>
                </ContentSection>
                <ContentSection
                  label={`Generated Applications (${childApps.length})`}
                  gridSizes={{ xs: 12 }}
                >
                  {childApps.length > 0 ? (
                    <Box mt={1}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell style={compactHeaderStyle}>
                              Name
                            </TableCell>
                            <TableCell style={compactHeaderStyle}>
                              Health
                            </TableCell>
                            <TableCell style={compactHeaderStyle}>
                              Sync
                            </TableCell>
                            <TableCell style={compactHeaderStyle}>
                              Last Sync
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedApps.map(app => (
                            <TableRow key={app.name}>
                              <TableCell style={compactCellStyle}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gridGap={6}
                                >
                                  <Typography variant="body2">
                                    {app.name}
                                  </Typography>
                                  {app.argocdUrl && (
                                    <Tooltip title="Open in ArgoCD" arrow>
                                      <Link
                                        href={app.argocdUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Open in ArgoCD"
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          opacity: 0.9,
                                        }}
                                      >
                                        <ArgoCDLogoSmall />
                                      </Link>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell style={compactCellStyle}>
                                <Box display="flex" alignItems="center">
                                  <StatusIndicator
                                    type={statusIndicatorType(app.healthStatus)}
                                    label={app.healthStatus || 'Unknown'}
                                  />
                                  <DegradedTooltip
                                    resources={app.degradedResources}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell style={compactCellStyle}>
                                <StatusIndicator
                                  type={statusIndicatorType(app.syncStatus)}
                                  label={app.syncStatus || 'Unknown'}
                                />
                              </TableCell>
                              <TableCell style={compactCellStyle}>
                                <Tooltip
                                  title={
                                    app.lastSync
                                      ? DateTime.fromISO(
                                          app.lastSync,
                                        ).toLocaleString(DateTime.DATETIME_FULL)
                                      : ''
                                  }
                                  arrow
                                >
                                  <Typography variant="body2">
                                    {formatTimestamp(app.lastSync)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <TablePager
                        page={page}
                        setPage={setPage}
                        total={childApps.length}
                        rowsPerPage={ROWS_PER_PAGE}
                      />
                    </Box>
                  ) : (
                    <Box mt={2} mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        No applications generated yet.
                      </Typography>
                    </Box>
                  )}
                </ContentSection>
                {childApps.length === 0 && (
                  <Alert severity="info" style={{ marginTop: 8 }}>
                    If this is unexpected, check your generator configuration.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
